import soundcloudConfig from "../../config/soundcloudConfig";
import { fetchJson } from "./http";
import { clearCachedClientId, readCachedClientId, saveCachedClientId } from "./storage";

let memoryClientId = null;
let inFlightRequest = null;
let refreshTimer = null;

async function fetchFreshClientId({ forceRefresh = false } = {}) {
  const endpoint = forceRefresh
    ? `${soundcloudConfig.CLIENT_ID_INTERNAL_ENDPOINT}?force=1`
    : soundcloudConfig.CLIENT_ID_INTERNAL_ENDPOINT;

  const payload = await fetchJson(endpoint);
  const clientId = String(payload?.clientId || "").trim();

  if (!soundcloudConfig.CLIENT_ID_REGEX.test(clientId)) {
    throw new Error("Invalid client_id returned from internal resolver.");
  }

  return clientId;
}

export async function getSoundCloudClientId({ forceRefresh = false } = {}) {
  const envFallback = String(soundcloudConfig.CLIENT_ID_ENV_FALLBACK || "").trim();
  const envFallbackValid =
    envFallback.length > 0 && soundcloudConfig.CLIENT_ID_REGEX.test(envFallback);

  if (envFallbackValid && !forceRefresh) {
    memoryClientId = envFallback;
    return memoryClientId;
  }

  if (!forceRefresh && memoryClientId) {
    return memoryClientId;
  }

  if (!forceRefresh) {
    const cached = readCachedClientId();
    if (cached?.clientId) {
      memoryClientId = cached.clientId;
      return memoryClientId;
    }
  }

  if (!inFlightRequest) {
    inFlightRequest = (async () => {
      try {
        const freshClientId = await fetchFreshClientId({ forceRefresh });
        memoryClientId = freshClientId;
        saveCachedClientId(freshClientId);
        return freshClientId;
      } catch (error) {
        if (envFallbackValid) {
          memoryClientId = envFallback;
          saveCachedClientId(envFallback);
          return envFallback;
        }
        throw error;
      } finally {
        inFlightRequest = null;
      }
    })();
  }

  return inFlightRequest;
}

export function resetSoundCloudClientId() {
  memoryClientId = null;
  clearCachedClientId();
}

export function startClientIdAutoRefresh() {
  if (typeof window === "undefined") return;
  if (refreshTimer) return;

  refreshTimer = window.setInterval(() => {
    getSoundCloudClientId({ forceRefresh: true }).catch(() => {
      // Silent fail; foreground requests can retry.
    });
  }, soundcloudConfig.CLIENT_ID_CACHE_TTL_MS);
}

export function stopClientIdAutoRefresh() {
  if (!refreshTimer || typeof window === "undefined") return;
  window.clearInterval(refreshTimer);
  refreshTimer = null;
}
